import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../assets/logo.png';

const letters = ['S', 'P', 'E'];

export default function Loader({ onDone }) {
  const [gone, setGone] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setGone(true);
      setTimeout(onDone, 600);
    }, 3200);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <AnimatePresence>
      {!gone && (
        <motion.div
          key="loader"
          exit={{ opacity: 0, scale: 1.03, filter: 'blur(5px)' }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          style={s.root}
        >
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.82 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.75, type: 'spring', bounce: 0.5 }}
            style={s.logoWrap}
          >
            <div style={s.ringOuter} />
            <div style={s.ringInner} />
            <div style={s.logoCircle}>
              <img src={logo} alt="SPE" style={s.logoImg} />
            </div>
          </motion.div>

          {/* Brand */}
          <div style={s.brand}>
            {/* S P E letters */}
            <div style={s.letters}>
              {letters.map((l, i) => (
                <motion.span
                  key={l}
                  initial={{ opacity: 0, y: 20, scaleY: 1.3, filter: 'blur(4px)' }}
                  animate={{ opacity: 1, y: 0, scaleY: 1, filter: 'blur(0px)' }}
                  transition={{
                    delay: 0.55 + i * 0.17,
                    duration: 0.55,
                    type: 'spring',
                    bounce: 0.35,
                  }}
                  style={s.letter}
                >
                  {l}
                </motion.span>
              ))}
            </div>

            {/* Divider */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2, duration: 0.5 }}
              style={s.divider}
            >
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: 40 }}
                transition={{ delay: 1.2, duration: 0.5 }}
                style={s.line}
              />
              <div style={s.dot} />
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: 40 }}
                transition={{ delay: 1.2, duration: 0.5 }}
                style={s.line}
              />
            </motion.div>

            {/* Tagline */}
            <motion.p
              initial={{ opacity: 0, letterSpacing: '0.5em' }}
              animate={{ opacity: 1, letterSpacing: '0.25em' }}
              transition={{ delay: 1.35, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              style={s.tag}
            >
              Shree Parasnath Enterprises
            </motion.p>
          </div>

          <style>{`
            @keyframes spe-spin     { to { transform: rotate(360deg); } }
            @keyframes spe-rev-spin { to { transform: rotate(-360deg); } }
            @keyframes spe-shimmer  {
              0%   { background-position: -200% center; }
              100% { background-position:  200% center; }
            }
            .spe-letter-inner {
              background: linear-gradient(135deg,#E91E8C 0%,#FF6B2B 50%,#FFD700 100%);
              background-size: 300% auto;
              -webkit-background-clip: text;
              -webkit-text-fill-color: transparent;
              background-clip: text;
              animation: spe-shimmer 3s linear infinite;
              display: inline-block;
            }
            .spe-ring-outer { animation: spe-spin     8s linear infinite; }
            .spe-ring-inner { animation: spe-rev-spin 5s linear infinite; }
          `}</style>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const PINK = '#E91E8C';

const s = {
  root: {
    position: 'fixed', inset: 0, zIndex: 9999,
    background: '#ffffff',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    gap: 0,
  },
  logoWrap: {
    position: 'relative',
    marginBottom: 28,
  },
  ringOuter: {
    className: 'spe-ring-outer',
    position: 'absolute', inset: -10,
    borderRadius: '50%',
    border: `1px solid rgba(233,30,140,0.2)`,
  },
  ringInner: {
    className: 'spe-ring-inner',
    position: 'absolute', inset: -5,
    borderRadius: '50%',
    border: `1px dashed rgba(233,30,140,0.12)`,
  },
  logoCircle: {
    width: 100, height: 100,
    borderRadius: '50%', overflow: 'hidden',
    border: `2px solid rgba(233,30,140,0.15)`,
  },
  logoImg: {
    width: '100%', height: '100%',
    objectFit: 'cover', display: 'block',
  },
  brand: {
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', gap: 10,
  },
  letters: {
    display: 'flex', gap: 6,
    alignItems: 'baseline', lineHeight: 1,
  },
  letter: {
    fontSize: 'clamp(2.8rem,8vw,4rem)',
    fontWeight: 800,
    background: 'linear-gradient(135deg,#E91E8C 0%,#FF6B2B 50%,#FFD700 100%)',
    backgroundSize: '300% auto',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    display: 'inline-block',
  },
  divider: {
    display: 'flex', alignItems: 'center', gap: 10,
  },
  line: {
    height: 1,
    background: `linear-gradient(90deg,transparent,rgba(233,30,140,0.35),transparent)`,
    overflow: 'hidden',
  },
  dot: {
    width: 4, height: 4, borderRadius: '50%',
    background: 'linear-gradient(135deg,#E91E8C,#FFD700)',
    opacity: 0.6,
  },
  tag: {
    fontSize: '0.65rem',
    letterSpacing: '0.25em',
    color: '#999',
    fontWeight: 500,
    textTransform: 'uppercase',
    margin: 0,
  },
};